/**
 * Utility functions for error handling
 */

/**
 * Logs an error and throws it with a custom message
 * @param error - The original error
 * @param customMessage - Custom error message to use
 * @param additionalData - Additional data to log
 */
export function logAndThrow(
  error: any,
  customMessage: string,
  additionalData?: Record<string, any>
): never {
  console.error(customMessage, {
    originalError: error,
    message: error?.message,
    code: error?.code,
    details: error?.details,
    hint: error?.hint,
    ...additionalData,
  });

  // If it's already an Error instance, modify its message
  if (error instanceof Error) {
    error.message = `${customMessage}: ${error.message}`;
    throw error;
  }

  // Otherwise create a new error
  const newError = new Error(customMessage);
  (newError as any).originalError = error;
  throw newError;
}

/**
 * Formats an error message for display to users
 * @param error - The error to format
 * @param fallbackMessage - Fallback message if error can't be formatted
 */
export function formatErrorMessage(
  error: any,
  fallbackMessage: string = 'An unexpected error occurred'
): string {
  if (!error) return fallbackMessage;

  // Check for common Supabase error formats
  if (error?.message) {
    // Handle specific error codes
    if (error.code === '23505') {
      return 'This record already exists';
    }
    if (error.code === '23503') {
      return 'Cannot perform this operation due to related records';
    }
    if (error.code === '42501') {
      return 'You do not have permission to perform this action';
    }

    return error.message;
  }

  // Check if it's a string
  if (typeof error === 'string') {
    return error;
  }

  return fallbackMessage;
}

/**
 * Safely extracts error details for logging
 * @param error - The error to extract details from
 */
export function extractErrorDetails(error: any): Record<string, any> {
  if (!error) return {};

  const details: Record<string, any> = {};

  if (error instanceof Error) {
    details.message = error.message;
    details.name = error.name;
    details.stack = error.stack;
  }

  // Supabase specific error properties
  if (error?.code) details.code = error.code;
  if (error?.details) details.details = error.details;
  if (error?.hint) details.hint = error.hint;
  if (error?.status) details.status = error.status;
  if (error?.statusText) details.statusText = error.statusText;

  return details;
}